/**
 * Helpers for residential + commercial energy series that must align by year
 * before computing totals, shares, or comparisons.
 */

export type SectorYearValue = { year: number; value: number };

function sumDatasetsAtIndex(datasets: any[], index: number): number {
  return (datasets || []).reduce((sum: number, d: any) => {
    const v = Number(d?.data?.[index]);
    return sum + (Number.isFinite(v) ? v : 0);
  }, 0);
}

export function sectorTotalsByYear(chartData: {
  labels?: number[];
  datasets?: any[];
}): Map<number, number> {
  const labels = (chartData?.labels || []) as number[];
  const datasets = chartData?.datasets || [];
  const map = new Map<number, number>();
  labels.forEach((year, i) => {
    const total = sumDatasetsAtIndex(datasets, i);
    if (total > 0) map.set(year, total);
  });
  return map;
}

/** Latest year where both sector maps have positive values. */
export function latestCommonPositiveYear(
  resByYear: Map<number, number>,
  comByYear: Map<number, number>
): number | null {
  const years = [...resByYear.keys()].filter((y) => {
    const r = resByYear.get(y) ?? 0;
    const c = comByYear.get(y) ?? 0;
    return r > 0 && c > 0;
  });
  if (!years.length) return null;
  return Math.max(...years);
}

export function sharesForYear(
  resByYear: Map<number, number>,
  comByYear: Map<number, number>,
  year: number
): { residentialShare: number; commercialShare: number; total: number } | null {
  const r = resByYear.get(year) ?? 0;
  const c = comByYear.get(year) ?? 0;
  const total = r + c;
  if (r <= 0 || c <= 0 || total <= 0) return null;
  return {
    total,
    residentialShare: (r / total) * 100,
    commercialShare: (c / total) * 100,
  };
}

export function percentChangeSinceBaseline(
  latest: number,
  baseline: number | null | undefined
): number | null {
  if (!Number.isFinite(latest) || !Number.isFinite(baseline) || baseline! <= 0) return null;
  return ((latest - baseline!) / baseline!) * 100;
}

export function valueAtYear(map: Map<number, number>, year: number): number | null {
  const v = map.get(year);
  return v != null && v > 0 ? v : null;
}

/** Latest year with a positive value in a single-sector series. */
export function latestPositiveYear(map: Map<number, number>): number | null {
  const years = [...map.keys()].filter((y) => (map.get(y) ?? 0) > 0);
  if (!years.length) return null;
  return Math.max(...years);
}

export type CompatiblePerCapitaPair = {
  year: number;
  local: number;
  compare: number;
};

/** Match local and compare per-capita values on the same year (both positive). */
export function latestCompatiblePerCapitaPair(
  localLabels: number[],
  localValues: number[],
  compareLabels: number[],
  compareValues: number[]
): CompatiblePerCapitaPair | null {
  const localMap = new Map<number, number>();
  localLabels.forEach((y, i) => {
    const v = Number(localValues[i]);
    if (v > 0) localMap.set(y, v);
  });
  const compareMap = new Map<number, number>();
  compareLabels.forEach((y, i) => {
    const v = Number(compareValues[i]);
    if (v > 0) compareMap.set(y, v);
  });
  const common = [...localMap.keys()].filter((y) => compareMap.has(y));
  if (!common.length) return null;
  const year = Math.max(...common);
  return { year, local: localMap.get(year)!, compare: compareMap.get(year)! };
}
